<script setup lang="ts">
import {
  ChevronRight,
  Clock3,
  Star,
  Trash2,
  UserRound,
  X,
} from "lucide-vue-next";
import type { PublicWorkerReview } from "~/types/public-worker";
import { toTitleCase } from "~/utils/search-catalog";
import { formatPersonName } from "~/utils/worker-profile";

const props = defineProps<{
  review: PublicWorkerReview;
  actionsDisabled?: boolean;
}>();

const emit = defineEmits<{
  delete: [review: PublicWorkerReview];
}>();

const { locale, t } = useI18n();

const imageBroken = ref(false);
const detailsOpen = ref(false);
const commentElement = ref<HTMLElement | null>(null);
const commentIsLong = ref(false);

let commentResizeObserver: ResizeObserver | null = null;

const clientName = computed(() => {
  if (!props.review.client) {
    return t("worker_profile.anonymous_client");
  }

  return formatPersonName(
    props.review.client.name,
    props.review.client.lastname,
  );
});

const clientImageSrc = computed(
  () => props.review.client?.profileImageUrl?.trim() || "",
);
const hasClientImage = computed(
  () => Boolean(clientImageSrc.value) && !imageBroken.value,
);

const photoUrls = computed(() =>
  props.review.photos.filter((photo): photo is string => Boolean(photo)),
);

const isPending = computed(() => props.review.moderationStatus === "pending");

const reviewBadge = computed(() => {
  if (props.review.serviceName) {
    return catalogLabel("services", props.review.serviceName);
  }

  if (props.review.categoryName) {
    return catalogLabel("category", props.review.categoryName);
  }

  return "";
});

function catalogLabel(type: "services" | "category", value: string) {
  const key = `${type}.${value}`;
  const translated = t(key);

  return translated === key ? toTitleCase(value) : translated;
}

function formatDate(value: string) {
  const date = new Date(value);
  const diffDays = Math.round((date.getTime() - Date.now()) / 86_400_000);

  if (Math.abs(diffDays) < 30) {
    return new Intl.RelativeTimeFormat(locale.value, {
      numeric: "auto",
    }).format(diffDays, "day");
  }

  return new Intl.DateTimeFormat(locale.value, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

async function checkCommentOverflow() {
  await nextTick();

  const element = commentElement.value;

  if (!element || !props.review.comment) {
    commentIsLong.value = false;
    return;
  }

  commentIsLong.value = element.scrollHeight > element.clientHeight + 1;
}

function openDetails() {
  detailsOpen.value = true;
}

function closeDetails() {
  detailsOpen.value = false;
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && detailsOpen.value) {
    closeDetails();
  }
}

watch(
  () => props.review.comment,
  async () => {
    commentIsLong.value = false;
    await checkCommentOverflow();
  },
);

watch(detailsOpen, open => {
  if (!import.meta.client) return;

  if (open) {
    window.addEventListener("keydown", handleKeydown);
  } else {
    window.removeEventListener("keydown", handleKeydown);
  }
});

onMounted(async () => {
  await checkCommentOverflow();

  if (!commentElement.value) return;

  commentResizeObserver = new ResizeObserver(() => {
    void checkCommentOverflow();
  });

  commentResizeObserver.observe(commentElement.value);
});

onBeforeUnmount(() => {
  commentResizeObserver?.disconnect();
  commentResizeObserver = null;

  if (import.meta.client) {
    window.removeEventListener("keydown", handleKeydown);
  }
});
</script>

<template>
  <article
    class="flex h-full min-w-0 flex-col rounded-xl border bg-white p-5 sm:p-6"
    :class="review.isOwn ? 'border-primary-200' : 'border-app-border'"
  >
    <div class="flex items-start justify-between gap-4">
      <div class="flex min-w-0 items-center gap-3">
        <div
          class="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-100 text-sm font-semibold text-primary"
        >
          <img
            v-if="hasClientImage"
            :src="clientImageSrc"
            :alt="clientName"
            class="size-full object-cover"
            @error="imageBroken = true"
          />

          <UserRound
            v-else
            class="size-5 text-primary"
            :stroke-width="1.7"
            aria-hidden="true"
          />
        </div>

        <div class="min-w-0">
          <h3 class="truncate text-base font-semibold text-app-text">
            {{ clientName }}
          </h3>

          <div class="mt-1 flex gap-0.5" :aria-label="`${review.rating} / 5`">
            <Star
              v-for="star in 5"
              :key="star"
              class="size-4"
              :class="
                star <= review.rating
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-app-border'
              "
            />
          </div>
        </div>
      </div>

      <div class="flex shrink-0 items-center gap-2">
        <time class="whitespace-nowrap text-xs text-app-muted">
          {{ formatDate(review.createdAt) }}
        </time>

        <button
          v-if="review.isOwn"
          type="button"
          class="flex size-9 items-center justify-center rounded-lg text-app-muted transition hover:bg-red-50 hover:text-app-danger disabled:cursor-not-allowed disabled:opacity-50"
          :aria-label="$t('worker_profile.delete_review')"
          :title="$t('worker_profile.delete_review')"
          :disabled="actionsDisabled"
          @click="emit('delete', review)"
        >
          <Trash2 class="size-4" />
        </button>
      </div>
    </div>

    <div class="mt-3 flex flex-wrap items-center gap-2">
      <span
        v-if="review.isOwn"
        class="inline-flex rounded-md bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary"
      >
        {{ $t("worker_profile.your_review") }}
      </span>

      <span
        v-if="review.isOwn && isPending"
        class="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700"
      >
        <Clock3 class="size-3.5" />
        {{ $t("worker_profile.review_pending_moderation") }}
      </span>

      <span
        v-if="reviewBadge"
        class="inline-flex rounded-md bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary"
      >
        {{ reviewBadge }}
      </span>
    </div>

    <div v-if="review.comment" class="mt-4">
      <p
        ref="commentElement"
        class="line-clamp-5 whitespace-pre-wrap break-words text-sm leading-6 text-app-text/85 [overflow-wrap:anywhere]"
      >
        {{ review.comment }}
      </p>

      <button
        v-if="commentIsLong"
        type="button"
        class="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary transition hover:opacity-75"
        @click="openDetails"
      >
        {{ $t("worker_profile.show_more") }}
        <ChevronRight class="size-4" />
      </button>
    </div>

    <div v-if="photoUrls.length" class="mt-4 flex gap-2 overflow-x-auto">
      <el-image
        v-for="(photo, index) in photoUrls.slice(0, 4)"
        :key="photo"
        :src="photo"
        :preview-src-list="photoUrls"
        :initial-index="index"
        fit="cover"
        hide-on-click-modal
        show-progress
        class="size-20 shrink-0 cursor-pointer overflow-hidden rounded-lg bg-gray-100"
      />
    </div>

    <ClientOnly>
      <Teleport to="body">
        <Transition name="review-details">
          <div
            v-if="detailsOpen"
            class="fixed inset-0 z-[5100] flex items-center justify-center bg-black/55 p-4 backdrop-blur-[1px] sm:p-6"
            role="dialog"
            aria-modal="true"
            @click.self="closeDetails"
            @wheel.prevent
            @touchmove.self.prevent
          >
            <div
              class="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
              @wheel.stop
              @touchmove.stop
            >
              <button
                type="button"
                class="absolute right-4 top-4 z-10 flex size-10 items-center justify-center rounded-full bg-gray-100 text-app-text transition hover:bg-gray-200"
                @click="closeDetails"
              >
                <X class="size-5" />
              </button>

              <div class="overflow-y-auto px-5 py-6 sm:px-7 sm:py-7">
                <div class="flex min-w-0 items-center gap-3 pr-12">
                  <div
                    class="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-100 text-sm font-semibold text-primary"
                  >
                    <img
                      v-if="hasClientImage"
                      :src="clientImageSrc"
                      :alt="clientName"
                      class="size-full object-cover"
                      @error="imageBroken = true"
                    />

                    <UserRound
                      v-else
                      class="size-5 text-primary"
                      :stroke-width="1.7"
                      aria-hidden="true"
                    />
                  </div>

                  <div class="min-w-0">
                    <h2 class="truncate text-lg font-bold text-app-text">
                      {{ clientName }}
                    </h2>

                    <div class="mt-1 flex items-center gap-3">
                      <div class="flex gap-0.5">
                        <Star
                          v-for="star in 5"
                          :key="star"
                          class="size-4"
                          :class="
                            star <= review.rating
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-app-border'
                          "
                        />
                      </div>

                      <time class="text-xs text-app-muted">
                        {{ formatDate(review.createdAt) }}
                      </time>
                    </div>
                  </div>
                </div>

                <div v-if="reviewBadge" class="mt-4 flex flex-wrap gap-2">
                  <span
                    class="inline-flex rounded-md bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary"
                  >
                    {{ reviewBadge }}
                  </span>
                </div>

                <p
                  class="mt-5 whitespace-pre-wrap break-words text-sm leading-7 text-app-text/85 [overflow-wrap:anywhere] sm:text-base"
                >
                  {{ review.comment }}
                </p>

                <div
                  v-if="photoUrls.length"
                  class="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3"
                >
                  <el-image
                    v-for="(photo, index) in photoUrls"
                    :key="photo"
                    :src="photo"
                    :preview-src-list="photoUrls"
                    :initial-index="index"
                    fit="cover"
                    hide-on-click-modal
                    show-progress
                    class="aspect-square w-full cursor-pointer overflow-hidden rounded-xl bg-gray-100"
                  />
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </article>
</template>

<style scoped>
.review-details-enter-active,
.review-details-leave-active {
  transition: opacity 180ms ease;
}

.review-details-enter-active > div,
.review-details-leave-active > div {
  transition:
    transform 180ms ease,
    opacity 180ms ease;
}

.review-details-enter-from,
.review-details-leave-to {
  opacity: 0;
}

.review-details-enter-from > div,
.review-details-leave-to > div {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}
</style>
